import { AlertTriangle, Camera, CheckCircle, Star } from "lucide-react";
import { filterSourceDocCitationsFromSession } from "../../../../lib/citationFilters";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "../../../../components/ui/button";
import { type AuditSession } from "../../../../models/audit/auditSession";
import "github-markdown-css/github-markdown-light.css";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../../components/ui/accordion.tsx";
import {
  TooltipContent,
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
} from "../../../../components/ui/tooltip.tsx";
import { useMemo, useState, useEffect } from "react";
import { useAuth } from "../../../../contexts/authContext";
import { getIssueDisplayStatus } from "../utils/issueDisplayStatus";
import { AnalyticsInputIndicator } from "../../../../components/analytics/AnalyticsInputIndicator";

export interface DocumentMapEntry {
  document: AuditSession["documents"][number];
  citations: AuditSession["sourceRows"][number]["checkResults"][number]["citations"];
}

interface CheckResultCardProps {
  checkResult: AuditSession["sourceRows"][number]["checkResults"][number];
  columnName: string;
  check: AuditSession["procedure"]["columns"][number] | undefined;
  auditSession: AuditSession;
  sourceRowId?: string;
  onDocumentClick: (documentEntry: DocumentMapEntry) => void;
  showInstructions?: boolean;
  showAuthor?: boolean;
  authorName?: string;
  isAttributeTest?: boolean;
  hideSourceDocCitations?: boolean;
}

const snakeToTitle = (str: string) => {
  if (!str) return "";
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const CheckResultCard = ({
  checkResult,
  columnName,
  check,
  auditSession,
  onDocumentClick,
  showInstructions = false,
  showAuthor = false,
  hideSourceDocCitations = false,
}: CheckResultCardProps) => {
  // isAttributeTest prop available for future use to add visual indicators
  const { user, getUserInfo } = useAuth();
  const exceptions = useMemo(
    () =>
      checkResult.issues.filter(
        (issue) => issue.issueCategory !== "test_outcome",
      ),
    [checkResult.issues],
  );

  // Get citation documents with their citation information
  const citationDocuments = useMemo(() => {
    if (!checkResult?.citations || checkResult.citations.length === 0) {
      return [];
    }

    // Filter out citations for documents that have screenshot versions
    const filteredCitations = filterSourceDocCitationsFromSession(
      checkResult.citations,
      auditSession.documents,
      hideSourceDocCitations,
    );

    const flatDocumentCitations: Array<DocumentMapEntry> = [];

    filteredCitations.forEach((citation) => {
      // Find the document in the session based on the citation's documentId
      const sourceDocument = auditSession.documents.find(
        (doc) => doc.documentId === citation.documentId,
      );

      if (sourceDocument) {
        flatDocumentCitations.push({
          document: sourceDocument,
          citations: [citation],
        });
      }
    });
    return flatDocumentCitations;
  }, [checkResult?.citations, auditSession.documents, hideSourceDocCitations]);

  const [authorName, setAuthorName] = useState<string>("");

  const displayStatus = useMemo(() => {
    return getIssueDisplayStatus(checkResult.issues);
  }, [checkResult.issues]);

  useEffect(() => {
    const getAuthorName = async () => {
      if (checkResult.reviewerType === "system") {
        setAuthorName("Midship Agent");
      } else if (checkResult.reviewerType === "human") {
        // If the current user created this result, show their email
        if (user && checkResult.userId === user.id) {
          setAuthorName(user.email);
        } else if (checkResult.userId) {
          const userInfo = await getUserInfo(checkResult.userId);
          setAuthorName(userInfo.email);
        } else {
          setAuthorName("User Override");
        }
      } else {
        setAuthorName("");
      }
    };

    getAuthorName();
  }, [checkResult.reviewerType, checkResult.userId, user, getUserInfo]);

  return (
    <div className="flex flex-col border border-stone-300 rounded-md">
      <div>
        <div className="flex items-center justify-between bg-stone-100 rounded-t p-4 border-b border-stone-300 text-base">
          <span>{columnName}</span>

          {showAuthor && (
            <span className="text-sm text-stone-500">By {authorName}</span>
          )}
        </div>

        {showInstructions && check?.evaluatedColumn?.instructions && (
          <Accordion className="px-4" type="single" collapsible>
            <AccordionItem value="item-1" className="border-b-0">
              <AccordionTrigger className="text-sm cursor-pointer">
                Column Instructions
              </AccordionTrigger>
              <AccordionContent>
                {check?.evaluatedColumn?.instructions}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}

        <div className="space-y-4 p-4">
          {displayStatus === "na" ? (
            <div className="bg-gray-100 text-gray-800 border border-gray-200 p-2 rounded-md flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              N/A
            </div>
          ) : displayStatus === "requires_attention" ? (
            <div className="bg-orange-100 text-orange-800 border border-orange-200 p-2 rounded-md flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Requires Attention
            </div>
          ) : displayStatus === "issues_found" ? (
            <div className="bg-red-100 text-red-800 border border-red-200 p-2 rounded-md flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Issues Found
            </div>
          ) : (
            <div className="bg-green-100 text-green-800 border border-green-200 p-2 rounded-md flex items-center gap-2">
              <CheckCircle className="h-6 w-6" />
              No Issues Found
            </div>
          )}

          <div className="flex flex-col gap-4 px-4 py-6 bg-slate-100">
            {exceptions.length > 0 && (
              <>
                <div className="text-sm text-font-secondary font-mono">
                  Issues
                </div>
                <div className="flex flex-col gap-2">
                  {exceptions.map((issue, issueIndex) => (
                    <div key={issueIndex} className="flex items-start gap-2">
                      <AlertTriangle className="text-amber-700 h-4 w-4 mt-1" />
                      {snakeToTitle(issue.issueSubcategory)}
                    </div>
                  ))}
                </div>
              </>
            )}

            <div>
              <div className="text-sm text-font-secondary font-mono">
                Conclusion
              </div>
              <div className="mt-1 markdown-body !bg-slate-100">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {checkResult.reason}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          {/* Evidence Used section */}
          {citationDocuments.length > 0 && (
            <div className="mt-4">
              <div className="text-md font-semibold mb-2">Evidence Used</div>
              <div className="flex flex-wrap gap-2 relative">
                <TooltipProvider>
                  {citationDocuments.map((documentEntry, index) => (
                    <Tooltip
                      key={`${documentEntry.document.id}-${documentEntry.citations[0]?.id || index}`}
                    >
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          className={`max-w-xs overflow-hidden border bg-stone-100 hover:bg-stone-200 text-stone-800 border-stone-300`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDocumentClick(documentEntry);
                          }}
                        >
                          {(() => {
                            const name = documentEntry.document.name || "";
                            const hasXlsx = /xlsx/i.test(name);
                            const hasScreenshot =
                              /screenshot/i.test(name) ||
                              /screen[\s_-]?shot/i.test(name);
                            const isExcelScreenshot = hasXlsx && hasScreenshot;
                            const isKeyCitation =
                              documentEntry.citations[0]?.isKey === true;
                            return (
                              <span className="flex items-center gap-2 truncate w-full text-left">
                                {isKeyCitation && (
                                  <Star className="h-3 w-3 shrink-0 fill-amber-400 text-amber-400" />
                                )}
                                {isExcelScreenshot && (
                                  <Camera className="h-4 w-4 shrink-0" />
                                )}
                                <span className="truncate">
                                  {documentEntry.document.name}
                                </span>
                                <AnalyticsInputIndicator
                                  flagged={
                                    documentEntry.document.isAnalyticsInput
                                  }
                                />
                              </span>
                            );
                          })()}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {(() => {
                          const name = documentEntry.document.name || "";
                          const hasXlsx = /xlsx/i.test(name);
                          const hasScreenshot =
                            /screenshot/i.test(name) ||
                            /screen[\s_-]?shot/i.test(name);
                          const isExcelScreenshot = hasXlsx && hasScreenshot;
                          const isKeyCitation =
                            documentEntry.citations[0]?.isKey === true;
                          const summary =
                            documentEntry.citations[0]?.shortSummary ||
                            "No summary available";
                          return (
                            <div className="max-w-xs">
                              {isKeyCitation && (
                                <div className="text-amber-600 text-xs mb-1 flex items-center gap-1">
                                  <Star className="h-3 w-3 fill-amber-400" />
                                  Include in WP
                                </div>
                              )}
                              {isExcelScreenshot && (
                                <div className="text-amber-700 text-xs mb-1 flex items-center gap-1">
                                  <Camera className="h-3.5 w-3.5" />
                                  Detected as screenshot
                                </div>
                              )}
                              <div>{summary}</div>
                            </div>
                          );
                        })()}
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </TooltipProvider>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
